import { useState, useMemo } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useProject } from '@/contexts/ProjectContext';
import { useVulnerabilitiesQuery } from '@/hooks/useVulnerabilitiesQuery';
import { buildReviewCommentsIndex } from '@/lib/reviewCommentsIndex';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, ArrowRightLeft, ExternalLink, CornerDownRight } from 'lucide-react';
import type {
  ReviewComment,
  ReviewCommentStatus,
  ReviewCommentSource,
  ReviewSeverity,
  ReviewTargetType,
} from '@/types';

export interface EnrichedReviewComment extends ReviewComment {
  targetName: string | null;
  parentName: string | null;
  externalId: string | null;
  targetTab: 'threats' | 'diagrams' | 'notes' | 'project-info';
  targetTabLabel: 'Threats' | 'Diagrams' | 'Notes' | 'Project Info';
  diagramIndex: number | null;
  diagramSubTab: 'overview' | 'connections' | 'entities' | 'boundaries' | 'notes' | null;
  replies: ReviewComment[];
}

export interface ReviewTabProps {
  onNavigateToDiagram?: (
    diagramIndex: number,
    subTab: 'overview' | 'connections' | 'entities' | 'boundaries' | 'notes',
  ) => void;
}

const HANDOVER_LABELS: Record<string, string> = {
  isa_handover_major: 'ISA Handover (Major)',
  isa_handover_minor: 'ISA Handover (Minor)',
  po_handover: 'PO Handover',
  review_draft: 'Draft',
  review_ready_for_review: 'Ready for Review',
  review_reviewed: 'Reviewed',
  review_approved: 'Approved',
};

const SEVERITY_BADGE_CLASSES: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  note: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  suggested: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
};

export default function ReviewTab({ onNavigateToDiagram }: ReviewTabProps = {}) {
  const {
    reviewComments = [],
    handovers = [],
    diagrams = [],
    threats = [],
    notes = [],
    projectInfo,
    setActiveTab,
    setCurrentDiagram,
  } = useApp();
  const { projectId } = useProject();
  const { data: allVulns = [] } = useVulnerabilitiesQuery(projectId);

  const [statusFilter, setStatusFilter] = useState<ReviewCommentStatus | 'all'>('suggested');
  const [sourceFilter, setSourceFilter] = useState<ReviewCommentSource | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<ReviewSeverity | 'all'>('all');
  const [targetTypeFilter, setTargetTypeFilter] = useState<Exclude<ReviewTargetType, 'comment'> | 'all'>('all');

  const enrichedComments = useMemo<EnrichedReviewComment[]>(() => {
    const diagramsById = new Map<number, { name: string; index: number }>();
    const entitiesById = new Map<number, { name: string; diagramName: string; diagramIndex: number }>();
    const boundariesById = new Map<number, { name: string; diagramName: string; diagramIndex: number }>();
    const pathsById = new Map<number, { name: string; diagramName: string; diagramIndex: number }>();

    diagrams.forEach((d, idx) => {
      if (typeof d.id === 'number') {
        diagramsById.set(d.id, { name: d.name, index: idx });
      }
      for (const e of d.entities ?? []) {
        entitiesById.set(e.id, { name: e.name, diagramName: d.name, diagramIndex: idx });
      }
      for (const b of d.boundaries ?? []) {
        boundariesById.set(b.id, { name: b.name, diagramName: d.name, diagramIndex: idx });
      }
      for (const p of d.paths ?? []) {
        pathsById.set(p.id, { name: p.name, diagramName: d.name, diagramIndex: idx });
      }
    });

    const threatsById = new Map<number, string>();
    const threatExtById = new Map<number, string>();
    for (const v of allVulns) {
      if (typeof v.threatcat_threat_id === 'number') {
        threatsById.set(v.threatcat_threat_id, v.name);
        if (v.external_id) {
          threatExtById.set(v.threatcat_threat_id, v.external_id);
        }
      }
    }
    for (const t of threats) {
      if (typeof t.id === 'number') {
        threatsById.set(t.id, t.name);
      }
    }

    const notesById = new Map<number, { type: string; diagramId: number | null }>();
    for (const n of notes) {
      if (typeof n.id === 'number') {
        notesById.set(n.id, { type: n.type, diagramId: n.diagram_id ?? null });
      }
    }

    const resolveName = (ttype: ReviewTargetType | null | undefined, tid: number | null | undefined): string | null => {
      if (!ttype) return null;
      if (ttype === 'project_version') return projectInfo?.name ?? null;
      if (typeof tid !== 'number') return null;
      if (ttype === 'diagram') return diagramsById.get(tid)?.name ?? null;
      if (ttype === 'entity') return entitiesById.get(tid)?.name ?? null;
      if (ttype === 'boundary') return boundariesById.get(tid)?.name ?? null;
      if (ttype === 'path') return pathsById.get(tid)?.name ?? null;
      if (ttype === 'threat') return threatsById.get(tid) ?? null;
      if (ttype === 'note') return notesById.get(tid)?.type ?? null;
      return null;
    };

    const { threadedRootComments } = buildReviewCommentsIndex({
      reviewComments,
      diagrams,
      notes,
    });

    const roots: EnrichedReviewComment[] = [];

    for (const c of threadedRootComments) {
      const targetName = resolveName(c.target_type, c.target_id);
      let parentName = resolveName(c.parent_type, c.parent_id);
      let diagramIndex: number | null = null;
      let diagramSubTab: EnrichedReviewComment['diagramSubTab'] = null;

      if (c.target_type === 'diagram') {
        diagramIndex = diagramsById.get(c.target_id)?.index ?? null;
        diagramSubTab = 'overview';
      } else if (c.target_type === 'entity') {
        const info = entitiesById.get(c.target_id);
        if (!parentName && info) parentName = info.diagramName;
        diagramIndex = info?.diagramIndex ?? (c.parent_id ? diagramsById.get(c.parent_id)?.index ?? null : null);
        diagramSubTab = 'entities';
      } else if (c.target_type === 'boundary') {
        const info = boundariesById.get(c.target_id);
        if (!parentName && info) parentName = info.diagramName;
        diagramIndex = info?.diagramIndex ?? (c.parent_id ? diagramsById.get(c.parent_id)?.index ?? null : null);
        diagramSubTab = 'boundaries';
      } else if (c.target_type === 'path') {
        const info = pathsById.get(c.target_id);
        if (!parentName && info) parentName = info.diagramName;
        diagramIndex = info?.diagramIndex ?? (c.parent_id ? diagramsById.get(c.parent_id)?.index ?? null : null);
        diagramSubTab = 'connections';
      } else if (c.target_type === 'note') {
        const noteInfo = notesById.get(c.target_id);
        const diagId =
          noteInfo !== undefined
            ? noteInfo.diagramId
            : c.parent_type === 'diagram'
              ? (c.parent_id ?? null)
              : null;
        if (typeof diagId === 'number') {
          const diagInfo = diagramsById.get(diagId);
          if (!parentName && diagInfo) parentName = diagInfo.name;
          diagramIndex = diagInfo?.index ?? null;
          diagramSubTab = 'notes';
        }
      }

      let targetTab: EnrichedReviewComment['targetTab'] = 'project-info';
      let targetTabLabel: EnrichedReviewComment['targetTabLabel'] = 'Project Info';
      if (c.target_type === 'threat') {
        targetTab = 'threats';
        targetTabLabel = 'Threats';
      } else if (
        c.target_type === 'diagram' ||
        c.target_type === 'entity' ||
        c.target_type === 'boundary' ||
        c.target_type === 'path' ||
        (c.target_type === 'note' && diagramSubTab === 'notes')
      ) {
        targetTab = 'diagrams';
        targetTabLabel = 'Diagrams';
      } else if (c.target_type === 'note') {
        targetTab = 'notes';
        targetTabLabel = 'Notes';
      }

      roots.push({
        ...c,
        targetName,
        parentName,
        externalId: c.target_type === 'threat' ? (threatExtById.get(c.target_id) ?? null) : null,
        targetTab,
        targetTabLabel,
        diagramIndex,
        diagramSubTab,
      });
    }

    return roots;
  }, [reviewComments, diagrams, threats, notes, projectInfo, allVulns]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: enrichedComments.length,
      suggested: 0,
      resolved: 0,
      rejected: 0,
    };
    for (const c of enrichedComments) {
      counts[c.status] = (counts[c.status] || 0) + 1;
    }
    return counts;
  }, [enrichedComments]);

  const filteredComments = useMemo(() => {
    return enrichedComments.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
      if (severityFilter !== 'all' && c.severity !== severityFilter) return false;
      if (targetTypeFilter !== 'all' && c.target_type !== targetTypeFilter) return false;
      return true;
    });
  }, [enrichedComments, statusFilter, sourceFilter, severityFilter, targetTypeFilter]);

  const handleNavigate = (comment: EnrichedReviewComment) => {
    if (comment.diagramIndex !== null && setCurrentDiagram) {
      setCurrentDiagram(comment.diagramIndex);
    }
    if (comment.diagramIndex !== null && comment.diagramSubTab && onNavigateToDiagram) {
      onNavigateToDiagram(comment.diagramIndex, comment.diagramSubTab);
    }
    setActiveTab(comment.targetTab);
  };

  return (
    <div className="space-y-6">
      {/* Handover History */}
      {handovers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4" />
              Handover History
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {handovers.map((h, idx) => (
              <div
                key={h.id ?? idx}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border bg-muted/30 p-3 text-sm"
              >
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {HANDOVER_LABELS[h.type] || h.type}
                    </span>
                    <span className="font-medium">{h.submitter || 'System'}</span>
                    {h.created_at && (
                      <span className="text-xs text-muted-foreground">{h.created_at}</span>
                    )}
                  </div>
                  {h.comment && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{h.comment}</p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Review Comments Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {(['suggested', 'resolved', 'rejected', 'all'] as const).map((s) => {
            const isActive = statusFilter === s;
            const label = s.charAt(0).toUpperCase() + s.slice(1);
            return (
              <Button
                key={s}
                data-filter-status={s}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-8"
                onClick={() => setStatusFilter(s)}
              >
                {label} ({statusCounts[s] || 0})
              </Button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Source filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as ReviewCommentSource | 'all')}
            className="h-8 rounded-md border bg-background px-2 text-xs"
          >
            <option value="all">All sources</option>
            <option value="user">User</option>
            <option value="ai">AI</option>
            <option value="system">System</option>
          </select>

          <select
            aria-label="Severity filter"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as ReviewSeverity | 'all')}
            className="h-8 rounded-md border bg-background px-2 text-xs"
          >
            <option value="all">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="note">Note</option>
          </select>

          <select
            aria-label="Target type filter"
            value={targetTypeFilter}
            onChange={(e) =>
              setTargetTypeFilter(e.target.value as Exclude<ReviewTargetType, 'comment'> | 'all')
            }
            className="h-8 rounded-md border bg-background px-2 text-xs"
          >
            <option value="all">All targets</option>
            <option value="project_version">Project Version</option>
            <option value="diagram">Diagram</option>
            <option value="entity">Entity</option>
            <option value="boundary">Boundary</option>
            <option value="path">Path</option>
            <option value="threat">Threat</option>
            <option value="note">Note</option>
          </select>
        </div>
      </div>

      {/* Review Comments List */}
      {filteredComments.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
              <MessageSquare className="h-5 w-5" />
              <p className="text-sm">No review comments match the current filters.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredComments.map((comment) => (
            <Card key={comment.id}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        STATUS_BADGE_CLASSES[comment.status] || 'bg-muted text-foreground'
                      }`}
                    >
                      {comment.status}
                    </span>
                    {comment.severity && (
                      <span
                        className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                          SEVERITY_BADGE_CLASSES[comment.severity] || 'bg-muted text-foreground'
                        }`}
                      >
                        {comment.severity}
                      </span>
                    )}
                    <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {comment.source}
                    </span>
                    {comment.quality_check_type && (
                      <span className="inline-flex items-center rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                        {comment.quality_check_type}
                      </span>
                    )}
                    <span className="text-xs font-medium text-foreground">
                      {comment.target_type}
                      {comment.externalId ? ` (${comment.externalId})` : ''}
                      {comment.targetName ? `: ${comment.targetName}` : ''}
                      {comment.parentName ? ` in ${comment.parentName}` : ''}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {comment.created_by || comment.source}
                      {comment.created_at ? ` · ${comment.created_at}` : ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => handleNavigate(comment)}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Go to {comment.targetTabLabel}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <p className="text-sm whitespace-pre-wrap">{comment.description}</p>

                {comment.replies.length > 0 && (
                  <div className="space-y-2 border-l-2 border-muted pl-4 pt-1">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="rounded-md bg-muted/40 p-2.5 text-sm space-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <CornerDownRight className="h-3 w-3" />
                          <span className="font-medium text-foreground">
                            {reply.created_by || reply.source}
                          </span>
                          {reply.created_at && <span>· {reply.created_at}</span>}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{reply.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
